/**
 * ProtectedRoute - Route guard for authenticated routes
 * 
 * Redirects to login if user is not authenticated.
 * Shows loading state while checking authentication.
 */
import React, { memo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/hooks';
import { selectIsAuthenticated, selectAuthLoading } from '@/store/slices/authSlice';
import { ROUTES } from '@/config/routes';

interface ProtectedRouteProps {
  /** Redirect path if not authenticated (default: login) */
  redirectTo?: string;
  /** If true, requires admin role */
  requireAdmin?: boolean;
}

/**
 * ProtectedRoute Component
 * 
 * @example
 * // Basic usage
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/profile" element={<ProfilePage />} />
 * </Route>
 * 
 * @example
 * // Admin only route
 * <Route element={<ProtectedRoute requireAdmin />}>
 *   <Route path="/admin" element={<AdminPage />} />
 * </Route>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = memo(({
  redirectTo = ROUTES.LOGIN,
  requireAdmin = false,
}) => {
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading = useAppSelector(selectAuthLoading);
  const isAdmin = useAppSelector((state) => state.auth.isAdmin);

  // Show loading while checking auth state
  if (loading === 'loading') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        fontSize: '1.125rem',
        color: '#64748B',
      }}>
        Checking authentication...
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Pass the current location so we can redirect back after login
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return (
      <Navigate 
        to={ROUTES.HOME} 
        replace 
      />
    );
  }

  // Render child routes
  return <Outlet />;
});

ProtectedRoute.displayName = 'ProtectedRoute';

export default ProtectedRoute;
